import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';
import {
  blogImageFrameClass,
  formatBlogDate,
  getBlogImage,
  getBlogPost,
  type BlogPost,
} from '@/lib/blog';
import {
  getAppStoreUrl,
  getPlayStoreUrl,
  getUtmParamsFromUrl,
} from '@/lib/urls';

function breadcrumbJsonLd(post: BlogPost) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://harborcoparent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://harborcoparent.com/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: `https://harborcoparent.com/blog/${post.slug}`,
      },
    ],
  };
}

function postingJsonLd(post: BlogPost) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    mainEntityOfPage: `https://harborcoparent.com/blog/${post.slug}`,
    author: {
      '@type': 'Organization',
      name: 'Harbor',
      url: 'https://harborcoparent.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Harbor',
      url: 'https://harborcoparent.com',
    },
  };
}

function BlogPostPage({ post }: { post: BlogPost }) {
  useEffect(() => {
    track('Blog Post Screen', { slug: post.slug });
  }, [post.slug]);

  const utm = getUtmParamsFromUrl();
  const location = 'Blog Post';
  const image = getBlogImage(post);
  const jsonLdScripts = [breadcrumbJsonLd(post), postingJsonLd(post)];

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={post.seoTitle}
        description={post.description}
        path={`/blog/${post.slug}`}
        ogType='article'
        jsonLd={jsonLdScripts}
      />
      {jsonLdScripts.map((data) => (
        <script
          key={String(data['@type'])}
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data).replace(/</g, '\\u003c'),
          }}
        />
      ))}
      <MarketingHeader />

      <article className='pt-32 pb-20'>
        <header className='mx-auto max-w-2xl px-6 text-center'>
          <p className='text-sm text-gray-500'>
            <time dateTime={post.publishedAt}>
              {formatBlogDate(post.publishedAt)}
            </time>
            <span className='mx-2'>·</span>
            <Link to='/blog' className='hover:text-primary'>
              Blog
            </Link>
          </p>
          <h1 className='mt-4 text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl'>
            {post.title}
          </h1>
          <p className='mt-5 text-xl leading-relaxed text-gray-600'>
            {post.excerpt}
          </p>
        </header>

        {image ? (
          <div className='mx-auto mt-12 max-w-3xl px-6'>
            <div className='flex justify-center rounded-3xl bg-primary/5 px-6 py-10'>
              <img
                src={image.src}
                alt={post.imageAlt ?? post.title}
                className={`h-auto max-h-[440px] w-auto object-contain ${blogImageFrameClass(
                  image
                )}`}
              />
            </div>
          </div>
        ) : null}

        <div className='mx-auto mt-12 max-w-2xl px-6'>
          {post.sections.map((section, index) => (
            <section
              key={section.heading || index}
              className={index > 0 ? 'mt-10' : ''}
            >
              {section.heading ? (
                <h2 className='text-xl font-bold text-gray-900'>
                  {section.heading}
                </h2>
              ) : null}
              {section.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className='mt-4 text-lg leading-relaxed text-gray-700'
                >
                  {paragraph}
                </p>
              ))}
              {section.bullets?.length ? (
                <ul className='mt-4 list-disc space-y-2 pl-5 text-lg text-gray-700'>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          {post.related.length ? (
            <div className='mt-16 border-t border-gray-200 pt-8'>
              <p className='text-sm font-semibold text-gray-500'>Related</p>
              <ul className='mt-4 space-y-3'>
                {post.related.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className='group'>
                      <span className='font-semibold text-primary group-hover:underline'>
                        {link.label}
                      </span>
                      <span className='text-gray-600'>
                        {' '}
                        · {link.description}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </article>

      <section className='bg-primary py-14'>
        <div className='mx-auto max-w-3xl px-6 text-center'>
          <h2 className='text-3xl font-bold text-white'>Try Harbor free</h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-white/85'>
            Download Harbor on iOS or Android, or sign up on web.
          </p>
          <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
            <a
              href={getAppStoreUrl(`blog-${post.slug}`, utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() =>
                track('Click App Store', { location, slug: post.slug })
              }
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
            >
              <FaApple size={18} />
              App Store
            </a>
            <a
              href={getPlayStoreUrl(`blog-${post.slug}`, utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() =>
                track('Click Google Play', { location, slug: post.slug })
              }
              className='inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
            >
              <FaGooglePlay size={18} />
              Google Play
            </a>
            <Link
              to='/auth/sign-up'
              onClick={() =>
                track('Click Sign Up', { location, slug: post.slug })
              }
              className='inline-flex items-center justify-center rounded-lg border border-white px-5 py-3 font-semibold text-white hover:bg-white/10'
            >
              Sign up on web
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MissingPostPage({ slug }: { slug: string }) {
  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Post not found | Harbor'
        description='This Harbor blog post is not available.'
        path={`/blog/${slug}`}
        noindex
      />
      <MarketingHeader />
      <main className='mx-auto max-w-3xl px-6 py-32 text-center'>
        <h1 className='text-3xl font-bold text-gray-900'>Post not found</h1>
        <p className='mt-4 text-gray-600'>
          That post is not on the Harbor blog.
        </p>
        <Link
          to='/blog'
          className='mt-6 inline-block font-semibold text-primary hover:underline'
        >
          Back to the blog
        </Link>
      </main>
      <Footer />
    </div>
  );
}

function BlogSlugPage() {
  const { slug } = Route.useParams();
  const post = getBlogPost(slug);
  if (!post) return <MissingPostPage slug={slug} />;
  return <BlogPostPage post={post} />;
}

export const Route = createFileRoute('/blog/$slug')({
  component: BlogSlugPage,
});
